import { action, computed, observable, makeObservable } from 'mobx';
import { Constructor, Result } from '../../core';
import { Error } from '../../core/error';
import { RouteContext } from '../../routes/routeContext';

export interface IPageState {
  isAttached: boolean;
  status: PageStatus;
  routeContext: RouteContext | null;
  error: Error | null;
}

export enum PageStatus {
  Empty = 'Empty',
  Loading = 'Loading',
  Loaded = 'Loaded',
  Error = 'Error'
}

export function PageState<TBase extends Constructor>(Base: TBase) {

  class PageState extends Base implements IPageState {

    constructor(...args: ConstructorParameters<Constructor>) {
      super(...args);
      makeObservable<this, 'abortController' | 'abortSignal' | 'baseAttached' | 'baseDetached' | 'baseAbort' | 'setError' | 'setLoading' | 'setLoaded'>(this, {
        isAttached: observable,
        routeContext: observable,
        status: observable,
        error: observable,
        abortController: observable,
        abortSignal: computed,
        baseAttached: action,
        baseDetached: action,
        baseAbort: action,
        setError: action,
        setLoading: action,
        setLoaded: action,
      });
    }

    isAttached = false;

    routeContext: RouteContext | null = null;
    status: PageStatus = PageStatus.Empty;
    error: Error | null = null;

    protected abortController: AbortController | null = null;

    protected get abortSignal() {
      return this.abortController;
    }

    protected baseAttached(routeContext: RouteContext | null) {
      this.isAttached = true;
      this.routeContext = routeContext;
      this.abortController = new AbortController();
    }

    protected baseDetached() {
      this.baseReset();
    }

    protected baseAbort() {
      const abortCtrl = this.abortController;
      if (abortCtrl)
        abortCtrl.abort();
      this.abortController = null;
    }

    protected setError(err: Error) {
      this.error = err;
      this.status = PageStatus.Error;
    }

    protected setLoading() {
      this.error = null;
      this.status = PageStatus.Loading;
    }

    protected setLoaded() {
      this.error = null;
      this.status = PageStatus.Loaded;
    }

    protected handleError(err: Error): Result<never, Error> {
      this.setError(err);
      return [null, err];
    }

    protected baseReset() {
      this.error = null;
      this.status = PageStatus.Empty;
      this.isAttached = false;
      this.routeContext = null;
      this.baseAbort();
    }
  }

  return PageState;
}